import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// ────────────────────────────────────────────────────────────────────────
// XalucaLogoOverlay — anchors the Grup Xaluca brand logo on every
// eligible <img> across the public site.
//
// The logo is injected as an absolutely-positioned sibling **inside the
// image's own container** (or a freshly-created wrapper if the image is
// loose in the layout). The CSS in `index.css` provides the responsive
// sizing, the padding from the edges and the fully-opaque rendering the
// brand expects.
//
// Smart corner selection: by default the logo goes in the top-right. If
// that corner is already occupied by a label / badge / button / icon /
// caption / overlay, the engine cycles through top-left → bottom-right →
// bottom-left and picks the first free corner. If every corner is busy,
// the corner with the least conflict wins so the watermark always shows.
//
// Skipped, in order:
//   1. Admin routes (`/admin/*`) — tooling UI only
//   2. The site header, chatbot widget, floating language switcher
//   3. Leaflet maps and modal/dialog contents
//   4. Tiny icons / avatars (< 110px on either side)
//   5. Anything explicitly opted out via `data-no-xaluca-logo="true"`
// ────────────────────────────────────────────────────────────────────────

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/zezo764i_Grup_Xaluca_logo.png.webp";

const MIN_SIDE = 110;
const MARK_CLASS = "xaluca-logo-mark";
const PROCESSED_ATTR = "data-xaluca-logo-checked";
const CORNER_ORDER = ["tr", "tl", "br", "bl"];

const isInsideSelector = (el, sel) => !!(el.closest && el.closest(sel));

const isOptedOut = (el) =>
  isInsideSelector(el, '[data-no-xaluca-logo="true"]') ||
  isInsideSelector(el, "header") ||
  isInsideSelector(el, ".leaflet-container") ||
  isInsideSelector(el, '[role="dialog"]') ||
  isInsideSelector(el, '[data-testid="chatbot-widget"]') ||
  isInsideSelector(el, '[data-testid="floating-language-switcher"]');

const isVisibleRect = (rect) => rect.width >= MIN_SIDE && rect.height >= MIN_SIDE;

const isCurrentlyHidden = (el) => {
  let node = el;
  while (node && node !== document.body) {
    if (node.getAttribute && node.getAttribute("aria-hidden") === "true") return true;
    const cs = window.getComputedStyle(node);
    if (cs.display === "none") return true;
    if (cs.visibility === "hidden") return true;
    if (parseFloat(cs.opacity) === 0) return true;
    node = node.parentElement;
  }
  return false;
};

// Closest positioned ancestor — the natural overlay frame.
const positionedAncestor = (el) => {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const cs = window.getComputedStyle(node);
    if (cs.position && cs.position !== "static") return node;
    node = node.parentElement;
  }
  return null;
};

// Find the host element the logo will be anchored to.
//   • For positioned (overflowing) images — e.g. Ken-Burns heroes whose
//     own rect is larger than the section — the visible box is the
//     positioned parent (the section clips via `overflow: hidden`).
//   • Otherwise we walk up to the smallest container whose bounding rect
//     still matches the image's (within 6%), which yields the natural
//     thumbnail frame.
//   • If neither applies, the caller wraps the image with a fresh span.
const findHost = (img, rect) => {
  const cs = window.getComputedStyle(img);
  if (cs.position === "absolute" || cs.position === "fixed") {
    const parent = positionedAncestor(img);
    if (parent) return parent;
  }
  let node = img.parentElement;
  let host = null;
  while (node && node !== document.body) {
    const r = node.getBoundingClientRect();
    const wDiff = Math.abs(r.width - rect.width) / Math.max(rect.width, 1);
    const hDiff = Math.abs(r.height - rect.height) / Math.max(rect.height, 1);
    if (wDiff > 0.06 || hDiff > 0.06) break;
    host = node;
    node = node.parentElement;
  }
  return host;
};

const ensurePositioned = (host) => {
  const cs = window.getComputedStyle(host);
  if (cs.position === "static") host.style.position = "relative";
};

const wrapImage = (img) => {
  const wrap = document.createElement("span");
  wrap.style.position = "relative";
  wrap.style.display = "inline-block";
  wrap.dataset.xalucaWrapper = "true";
  img.parentNode.insertBefore(wrap, img);
  wrap.appendChild(img);
  return wrap;
};

// "Meaningful overlay" = an element that visually occupies a corner of
// the host, *not* a gradient / scrim filling the whole image and *not*
// the image itself or the logo we're about to inject.
const isMeaningfulOverlay = (node, img, hRect) => {
  if (node === img) return false;
  if (node.tagName === "IMG") return false;
  if (node.classList && node.classList.contains(MARK_CLASS)) return false;
  // Skip descendants of our own logo
  if (node.closest && node.closest(`.${MARK_CLASS}`)) return false;
  // Skip the wrapping anchor / button that contains the image — it's the
  // host itself or part of the natural card frame, not a corner element.
  if (node.contains && node.contains(img)) return false;
  const cs = window.getComputedStyle(node);
  if (cs.display === "none" || cs.visibility === "hidden") return false;
  if (parseFloat(cs.opacity) === 0) return false;
  const r = node.getBoundingClientRect();
  if (r.width < 6 || r.height < 6) return false;
  // Gradient / scrim covering most of the host — not a corner badge
  if (r.width > hRect.width * 0.85 && r.height > hRect.height * 0.85) return false;
  // Must be meaningful: text, interactive, or with a visible
  // background / border. Pure invisible structural <div>s don't count.
  const text = (node.textContent || "").trim();
  if (text.length > 0) return true;
  if (["BUTTON", "A", "INPUT", "LABEL", "SVG"].includes(node.tagName)) return true;
  const hasBg =
    cs.backgroundImage !== "none" ||
    (cs.backgroundColor &&
      cs.backgroundColor !== "rgba(0, 0, 0, 0)" &&
      cs.backgroundColor !== "transparent");
  const borderTotal =
    parseFloat(cs.borderTopWidth || 0) +
    parseFloat(cs.borderRightWidth || 0) +
    parseFloat(cs.borderBottomWidth || 0) +
    parseFloat(cs.borderLeftWidth || 0);
  return hasBg || borderTotal > 0;
};

// Rectangle of a corner-shaped probe area within the host's bounding box.
const cornerRect = (hRect, cornerId, size) => {
  switch (cornerId) {
    case "tr":
      return { top: hRect.top, right: hRect.right, bottom: hRect.top + size, left: hRect.right - size };
    case "tl":
      return { top: hRect.top, right: hRect.left + size, bottom: hRect.top + size, left: hRect.left };
    case "br":
      return { top: hRect.bottom - size, right: hRect.right, bottom: hRect.bottom, left: hRect.right - size };
    case "bl":
      return { top: hRect.bottom - size, right: hRect.left + size, bottom: hRect.bottom, left: hRect.left };
    default:
      return null;
  }
};

const rectsIntersect = (a, b) =>
  !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);

const overlapArea = (a, b) => {
  const w = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const h = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return w * h;
};

// Pick the first corner whose probe area is free of meaningful overlays.
// If every corner has something, pick the corner with the least overlap
// area so the logo is always shown but in the least intrusive spot.
const chooseCorner = (img, host) => {
  // Manual override — allows a hero container to pin the watermark to a
  // specific corner regardless of smart-corner-detection (used on hero
  // blocks where the play button is placed next to the logo intentionally,
  // to keep the two brand elements together).
  const forced = host.getAttribute && host.getAttribute("data-xaluca-corner");
  if (forced && CORNER_ORDER.includes(forced)) return forced;

  const hRect = host.getBoundingClientRect();
  if (hRect.width === 0 || hRect.height === 0) return "tr";
  const probe = Math.max(60, Math.min(140, Math.min(hRect.width, hRect.height) * 0.32));
  const overlays = [];
  for (const node of host.querySelectorAll("*")) {
    if (!isMeaningfulOverlay(node, img, hRect)) continue;
    overlays.push(node.getBoundingClientRect());
  }
  if (overlays.length === 0) return "tr";
  const scores = {};
  for (const id of CORNER_ORDER) {
    const c = cornerRect(hRect, id, probe);
    if (!c) { scores[id] = Infinity; continue; }
    let any = false;
    let area = 0;
    for (const o of overlays) {
      if (rectsIntersect(o, c)) {
        any = true;
        area += overlapArea(o, c);
      }
    }
    if (!any) return id; // first clean corner wins
    scores[id] = area;
  }
  // All corners occupied — pick the least bad one (CORNER_ORDER is the
  // tie-break so the result is deterministic).
  let best = CORNER_ORDER[0];
  let bestScore = scores[best];
  for (const id of CORNER_ORDER) {
    if (scores[id] < bestScore) { bestScore = scores[id]; best = id; }
  }
  return best;
};

const renderLogo = (host, corner) => {
  // Don't double-inject
  for (const child of host.children) {
    if (child.classList && child.classList.contains(MARK_CLASS)) return;
  }
  const mark = document.createElement("span");
  mark.className = MARK_CLASS;
  mark.dataset.corner = corner;
  mark.setAttribute("aria-hidden", "true");
  // Opt out of the AutoImageEditor. The Xaluca logo mark is a purely
  // decorative brand-overlay watermark that gets sprayed onto every
  // eligible <img> at runtime — it should never appear as an
  // editorial slot in /admin/images.
  mark.setAttribute("data-no-cms-edit", "true");
  const inner = document.createElement("img");
  inner.src = LOGO_URL;
  inner.alt = "";
  inner.draggable = false;
  inner.loading = "lazy";
  inner.setAttribute("data-no-cms-edit", "true");
  mark.appendChild(inner);
  host.appendChild(mark);
};

const processImage = (img) => {
  if (!img || img.tagName !== "IMG") return;
  if (img.getAttribute(PROCESSED_ATTR) === "1") return;
  if (isOptedOut(img)) return;
  if (img.closest && img.closest(`.${MARK_CLASS}`)) return;

  const rect = img.getBoundingClientRect();
  if (!isVisibleRect(rect)) return;
  if (isCurrentlyHidden(img)) return;

  const host = findHost(img, rect) || wrapImage(img);
  ensurePositioned(host);
  const corner = chooseCorner(img, host);
  renderLogo(host, corner);
  img.setAttribute(PROCESSED_ATTR, "1");
};

const sweep = () => {
  document.querySelectorAll("img").forEach(processImage);
};

const reset = () => {
  document.querySelectorAll(`img[${PROCESSED_ATTR}]`).forEach((img) =>
    img.removeAttribute(PROCESSED_ATTR)
  );
  document.querySelectorAll(`.${MARK_CLASS}`).forEach((mark) => mark.remove());
};

const XalucaLogoOverlay = () => {
  const location = useLocation();
  const adminMode = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (adminMode) {
      reset();
      return undefined;
    }
    sweep();
    const retry1 = window.setTimeout(sweep, 400);
    const retry2 = window.setTimeout(sweep, 1200);

    const observer = new MutationObserver((muts) => {
      let interesting = false;
      for (const m of muts) {
        if (m.type === "childList") {
          for (const n of m.addedNodes) {
            if (n.nodeType !== 1) continue;
            if (n.classList && n.classList.contains(MARK_CLASS)) continue;
            interesting = true;
            break;
          }
        } else if (m.type === "attributes") {
          if (m.target.classList && m.target.classList.contains(MARK_CLASS)) continue;
          if (m.attributeName === PROCESSED_ATTR) continue;
          interesting = true;
        }
        if (interesting) break;
      }
      if (interesting) sweep();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src"],
    });

    // Re-evaluate corners on viewport changes (badge positions can shift,
    // breakpoints flip layouts, etc.). Debounced so it doesn't thrash.
    let resizeT;
    const onResize = () => {
      window.clearTimeout(resizeT);
      resizeT = window.setTimeout(() => {
        reset();
        sweep();
      }, 220);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.clearTimeout(retry1);
      window.clearTimeout(retry2);
      window.clearTimeout(resizeT);
      window.removeEventListener("resize", onResize);
      observer.disconnect();
    };
  }, [adminMode, location.pathname]);

  useEffect(() => {
    reset();
    sweep();
  }, [location.pathname]);

  return null;
};

export default XalucaLogoOverlay;
